import { MouseEvent, useEffect, useState } from 'react';
import {
  useApi,
  identityApiRef,
  useRouteRef,
  IconComponent,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteRef,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import {
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  DEFAULT_NAMESPACE,
  GroupEntity,
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { resolveUserGroupRefs } from '@internal/backstage-catalog-utils';

export interface MyTeamsSidebarItemProps {
  singularTitle: string;
  pluralTitle: string;
  icon: IconComponent;
  onClick?: (ev: MouseEvent) => void;
}

/**
 * Same shape as @backstage/plugin-org's MyGroupsSidebarItem (single group ->
 * direct link, multiple -> expandable submenu), but marks whichever group
 * is the signed-in user's primary team (PAT-678/user/primary-team) with a
 * ★ prefix in the submenu — the stock component has no way to do this,
 * since SidebarSubmenuItem's `title` is a plain string prop, not a slot for
 * arbitrary markup, so there's no way to inject a star icon without
 * replacing the whole component.
 */
export function MyTeamsSidebarItem({
  singularTitle,
  pluralTitle,
  icon,
  onClick,
}: MyTeamsSidebarItemProps) {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);

  const [groups, setGroups] = useState<GroupEntity[]>();
  const [primaryTeamRef, setPrimaryTeamRef] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(async identity => {
        // Leadership (lead/pm/tech-lead/architect) counts as team membership
        // here too — the catalog only ever derives relations.hasMember from
        // spec.members, so a lead-only assignment (e.g. Tech Lead of a team
        // where you're not also a plain member) would otherwise be silently
        // missing from this list, even while starred as primary.
        const [groupRefs, userEntity] = await Promise.all([
          resolveUserGroupRefs(identity.userEntityRef, catalogApi),
          catalogApi.getEntityByRef(identity.userEntityRef),
        ]);
        const { items } = groupRefs.length
          ? await catalogApi.getEntitiesByRefs({
              entityRefs: groupRefs,
              fields: ['metadata', 'kind', 'spec.profile'],
            })
          : { items: [] };
        return { items, userEntity };
      })
      .then(({ items, userEntity }) => {
        if (cancelled) return;
        setGroups(items.filter((e): e is GroupEntity => Boolean(e)));
        setPrimaryTeamRef(
          userEntity?.metadata.annotations?.['user/primary-team'],
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi]);

  if (!groups?.length) {
    return null;
  }

  if (groups.length === 1) {
    const group = groups[0];
    return (
      <SidebarItem
        text={singularTitle}
        to={catalogEntityRoute(getCompoundEntityRef(group))}
        icon={icon}
        onClick={onClick}
      />
    );
  }

  return (
    <SidebarItem icon={icon} text={pluralTitle} onClick={onClick}>
      <SidebarSubmenu title={pluralTitle}>
        {groups.map(group => {
          const ref = stringifyEntityRef(group);
          const isPrimary = ref === primaryTeamRef;
          const entityDisplayName = entityPresentationApi.forEntity(group);
          const title =
            entityDisplayName.snapshot.primaryTitle ?? group.metadata.name;
          return (
            // SidebarSubmenuItem has no onClick prop, so a click on a team
            // inside the submenu is bubbled up via this wrapper instead —
            // this preserves the same "clicking anything under My Team(s)
            // tracks as My Team" behavior the parent SidebarItem's onClick
            // alone can't cover for these submenu-only entries. Unlike the
            // wrapper divs removed from the sidebar for PAT-928, this one sits
            // inside the hover-revealed submenu popup, not the sidebar's
            // main flex column, so it does not reintroduce the collapsed
            // rail spacing bug.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div key={ref} onClick={onClick} style={{ display: 'contents' }}>
              <SidebarSubmenuItem
                title={isPrimary ? `★ ${title}` : title}
                subtitle={
                  group.metadata.namespace !== DEFAULT_NAMESPACE
                    ? group.metadata.namespace
                    : undefined
                }
                to={catalogEntityRoute(getCompoundEntityRef(group))}
                icon={icon}
              />
            </div>
          );
        })}
      </SidebarSubmenu>
    </SidebarItem>
  );
}
